"use client"

import { useState } from "react"
import { FaStar, FaUser } from "react-icons/fa"

import { Paragraph } from "@ui-library/typography/Body"
import { cn } from "@utils/utils"

interface ReviewCardProps {
  name: string
  age?: number
  location?: string
  stars: number
  title?: string
  quote: string
  readMoreText?: string
}

export const ReviewCard = ({ name, age, location, stars, title, quote, readMoreText }: ReviewCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const displayName = age ? `${name}, ${age}` : name
  const hasReadMore = readMoreText && readMoreText.length > 0
  const displayQuote = isExpanded && hasReadMore ? `${quote}${readMoreText}` : quote

  return (
    <article className="flex h-full min-h-[332px] flex-col whitespace-break-spaces break-words rounded-2xl bg-white p-6 shadow-elevation-1">
      {/* Header with avatar and name */}
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-background">
          <FaUser className="h-5 w-5 text-neutral-50" />
        </div>
        <div>
          <p className="font-semibold text-on-surface">{displayName}</p>
          {location && <p className="text-sm text-neutral-50">{location}</p>}
        </div>
      </div>

      {/* Divider */}
      <hr className="mb-4 border-neutral-30" />

      {/* Star rating */}
      <div className="mb-4 flex gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <FaStar
            key={star}
            className={cn("h-5 w-5", star <= stars ? "text-primary-50" : "text-neutral-30")}
            aria-hidden="true"
          />
        ))}
        <span className="sr-only">{stars} av 5 stjärnor</span>
      </div>

      {/* Review content */}
      <div className="flex grow flex-col">
        {title && <h4 className="mb-2 text-lg font-semibold">{title}</h4>}
        <Paragraph size="small" className="text-neutral-70">
          &ldquo;{displayQuote}&rdquo;
        </Paragraph>

        {hasReadMore && !isExpanded && (
          <button
            onClick={() => setIsExpanded(true)}
            className="mt-2 self-start text-sm font-medium text-primary hover:underline"
          >
            Läs mer
          </button>
        )}
      </div>
    </article>
  )
}

export default ReviewCard
